import { Point as FabricPoint } from 'fabric';

import { Dimensions, SpreadGroundImage } from 'editor/src/store/design/types';

export function getSpreadGroundOffsets(image: SpreadGroundImage, contentDimensions: Dimensions, uniformScale = false) {
  // When the bg/fg image was rendered with a single uniform scale (see
  // SpreadGroundImageElement / addSpreadImage `uniformScale` mode), the label
  // rect's position in canvas space must use that same scale — otherwise the
  // pan computed here points at a label location that doesn't match where the
  // bg image was actually drawn.
  if (uniformScale) {
    const scale = Math.min(contentDimensions.width / image.width, contentDimensions.height / image.height);
    return {
      offsetLeft: Math.abs(image.left) * scale,
      offsetTop: Math.abs(image.top) * scale,
    };
  }
  const offsetLeft = (Math.abs(image.left) * contentDimensions.width) / image.width;
  const offsetTop = (Math.abs(image.top) * contentDimensions.height) / image.height;
  return { offsetTop, offsetLeft };
}

export function getSpreadImageZoom(imageDim: Dimensions, canvasDimensions: Dimensions) {
  return imageDim.width > imageDim.height
    ? canvasDimensions.width / imageDim.width
    : canvasDimensions.height / imageDim.height;
}

function getSpreadImageCoverZoom(imageDim: Dimensions, canvasDimensions: Dimensions) {
  return Math.max(canvasDimensions.width / imageDim.width, canvasDimensions.height / imageDim.height);
}

function getMinZoom(
  background: Dimensions | undefined,
  foreground: Dimensions | undefined,
  canvasDimensions: Dimensions,
  defaultZoom = 1,
) {
  const backgroundZoom = background ? getSpreadImageZoom(background, canvasDimensions) : defaultZoom;
  const foregroundZoom = foreground ? getSpreadImageZoom(foreground, canvasDimensions) : defaultZoom;
  return Math.min(backgroundZoom, foregroundZoom);
}

function getCoverZoom(
  background: Dimensions | undefined,
  foreground: Dimensions | undefined,
  canvasDimensions: Dimensions,
  defaultZoom = 1,
) {
  // Skip missing layers entirely — feeding them a defaultZoom of 1 would
  // dominate the Math.max when the present image needs to be scaled down
  // (bgZoom < 1), leaving the viewport at 1:1 and showing only a slice.
  const zooms: number[] = [];
  if (background) {
    zooms.push(getSpreadImageCoverZoom(background, canvasDimensions));
  }
  if (foreground) {
    zooms.push(getSpreadImageCoverZoom(foreground, canvasDimensions));
  }
  return zooms.length ? Math.max(...zooms) : defaultZoom;
}

function getThumbnailZoomAndPan(
  defaultZoom: 'spread' | 'auto' | 'cover' | number,
  noCentering: boolean,
  canvasDim: Dimensions,
  contentDimensions: Dimensions,
  background: {
    image: SpreadGroundImage | undefined;
    dimensions: Dimensions | undefined;
  },
  foreground: {
    image: SpreadGroundImage | undefined;
    dimensions: Dimensions | undefined;
  },
  uniformGroundScale = false,
): { zoom: number; center: FabricPoint; maxWidth: number; maxHeight: number } {
  if (defaultZoom === 'spread' || (defaultZoom !== 'auto' && defaultZoom !== 'cover')) {
    const zoom =
      defaultZoom === 'spread' ? getMinZoom(background.dimensions, foreground.dimensions, canvasDim) : defaultZoom;

    const diffX = (canvasDim.width - contentDimensions.width * zoom) / 2;
    const diffY = (canvasDim.height - contentDimensions.height * zoom) / 2;

    return {
      zoom,
      center: noCentering ? new FabricPoint(0, 0) : new FabricPoint(-diffX, -diffY),
      maxWidth: canvasDim.width,
      maxHeight: canvasDim.height,
    };
  }

  const zoom =
    defaultZoom === 'cover'
      ? getCoverZoom(background.dimensions, foreground.dimensions, canvasDim)
      : getMinZoom(background.dimensions, foreground.dimensions, canvasDim);
  const backgroundOffset =
    (background.image && getSpreadGroundOffsets(background.image, contentDimensions, uniformGroundScale)) || null;
  const foregroundOffset =
    (foreground.image && getSpreadGroundOffsets(foreground.image, contentDimensions, uniformGroundScale)) || null;

  const maxWidth =
    Math.max(background.dimensions?.width || 0, foreground.dimensions?.width || 0, contentDimensions.width) * zoom;
  const offsetLeft = Math.max(backgroundOffset?.offsetLeft || 0, foregroundOffset?.offsetLeft || 0, 0) * zoom;
  const maxHeight =
    Math.max(background.dimensions?.height || 0, foreground.dimensions?.height || 0, contentDimensions.height) * zoom;
  const offsetTop = Math.max(backgroundOffset?.offsetTop || 0, foregroundOffset?.offsetTop || 0, 0) * zoom;

  return {
    zoom,
    center: new FabricPoint(
      -offsetLeft + (maxWidth - canvasDim.width) / 2,
      -offsetTop + (maxHeight - canvasDim.height) / 2,
    ),
    maxWidth,
    maxHeight,
  };
}

export default getThumbnailZoomAndPan;
